import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { CloseRounded } from '@mui/icons-material';
import { useTenantStore } from '../../../stores/tenant.store';
import { formatDate } from '../../../lib/i18n-format';
import { programEnrollmentApi, type WorkItem } from '../../../lib/api/program-enrollment.api';

// ─── Lo que hay debajo del agregado ─────────────────────────────────────────
//
// La pantalla mostraba «61 / 126» y no había forma de llegar a QUÉ trabajos son.
// Acá están, en tres listas que se accionan distinto:
//
//   · Sin entregar        — lo que el ALUMNO debe.
//   · Esperando corrección — lo que el SEMINARIO debe. Nunca en la misma lista.
//   · Devueltas            — alguien que ya trabajó una vez y no volvió.
//
// Cada una ordenada por vencimiento, lo más viejo primero: una tarea que venció
// hace dos meses pesa distinto de una de ayer.

interface Props {
  sectionId: string | null;
  termId: string;
  onClose: () => void;
}

export function SectionWorkDetailDialog({ sectionId, termId, onClose }: Props): JSX.Element {
  // UN SOLO NAMESPACE DECLARADO, y `common:` con prefijo explícito. Todos los
  // namespaces se cargan al iniciar i18next, así que el prefijo resuelve — y el
  // detector de claves faltantes sólo entiende esta forma: con
  // `useTranslation(['sis','common'])` cae a `common` y reporta como rotas las
  // claves de `sis` que sí existen.
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();

  const detailQ = useQuery({
    queryKey: ['section-work-detail', currentTenantId, termId, sectionId],
    enabled: !!currentTenantId && !!sectionId,
    queryFn: () =>
      programEnrollmentApi.getSectionWorkDetail({
        tenantId: currentTenantId!,
        termId,
        sectionId: sectionId!,
      }),
    staleTime: 60_000,
  });

  const detail = detailQ.data;

  return (
    <Dialog open={!!sectionId} onClose={onClose} maxWidth="md" fullWidth>
      {/* CON BOTÓN DE CERRAR. Sin él sólo se sale por Esc o clic afuera, y este
          diálogo es alto: en un móvil «afuera» casi no existe, y quedarse
          atrapado en una lista de 64 nombres es una trampa. */}
      <DialogTitle sx={{ pr: 6 }}>
        <IconButton
          onClick={onClose}
          aria-label={t('common:actions.close')}
          size="small"
          sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
        >
          <CloseRounded fontSize="small" />
        </IconButton>
        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
          <Typography variant="h6" fontWeight={700}>
            {detail?.courseName ?? '…'}
          </Typography>
          {detail?.sectionCode && (
            <Typography variant="caption" color="text.secondary">
              {detail.sectionCode}
            </Typography>
          )}
          {detail?.facultyName && (
            <Typography variant="caption" color="text.secondary">
              · {detail.facultyName}
            </Typography>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        {detailQ.isLoading && (
          <Stack alignItems="center" py={6}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {detail && (
          <Stack spacing={3}>
            <WorkList
              title={t('reports.coverage.detail.missing')}
              help={t('reports.coverage.detail.missingHelp')}
              color="error"
              items={detail.missing}
              emptyLabel={t('reports.coverage.detail.none')}
            />
            <Divider />
            <WorkList
              title={t('reports.coverage.detail.awaiting')}
              help={t('reports.coverage.detail.awaitingHelp')}
              color="warning"
              items={detail.awaitingGrading}
              emptyLabel={t('reports.coverage.detail.none')}
              showSubmitted
            />
            {detail.returned.length > 0 && (
              <>
                <Divider />
                <WorkList
                  title={t('reports.coverage.detail.returned')}
                  help={t('reports.coverage.detail.returnedHelp')}
                  color="info"
                  items={detail.returned}
                  emptyLabel={t('reports.coverage.detail.none')}
                />
              </>
            )}
            {detail.drafts.length > 0 && (
              <>
                <Divider />
                <WorkList
                  title={t('reports.coverage.detail.drafts')}
                  help={t('reports.coverage.detail.draftsHelp')}
                  color="default"
                  items={detail.drafts}
                  emptyLabel={t('reports.coverage.detail.none')}
                />
              </>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** AGRUPADA POR TAREA y no una fila por alumno: «cinco no entregaron el ensayo»
 *  es una acción; cinco líneas con el mismo nombre de tarea son una lista que
 *  hay que leer entera para descubrir lo mismo. */
function WorkList({
  title,
  help,
  color,
  items,
  emptyLabel,
  showSubmitted,
}: {
  title: string;
  help: string;
  color: 'error' | 'warning' | 'info' | 'default';
  items: WorkItem[];
  emptyLabel: string;
  showSubmitted?: boolean;
}): JSX.Element {
  const byAssignment = new Map<string, WorkItem[]>();
  for (const item of items) {
    const list = byAssignment.get(item.assignmentId);
    if (list) list.push(item);
    else byAssignment.set(item.assignmentId, [item]);
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
        <Chip size="small" color={color} variant="outlined" label={items.length} />
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {help}
      </Typography>

      {items.length === 0 ? (
        <Typography variant="body2" color="text.disabled">
          {emptyLabel}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {[...byAssignment.values()].map((group) => (
            <Box key={group[0]!.assignmentId}>
              <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" fontWeight={600}>
                  {group[0]!.assignmentName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(group[0]!.dueAt)}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {group
                  .map((i) =>
                    showSubmitted && i.submittedAt
                      ? `${i.studentName} (${formatDate(i.submittedAt)})`
                      : i.studentName,
                  )
                  .join(' · ')}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
